import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';

type AfiScreen = 'afi-overview' | 'afi-review' | 'afi-activity' | 'afi-brand-voice' | 'afi-settings';

interface Props {
  domainId: string;
  onNavigate: (name: AfiScreen, tab?: string) => void;
}

const BUCKET_LABELS: Record<string, string> = {
  schema: 'Schema fixes',
  'meta-header': 'Meta / headers',
  citation: 'Citations',
  content: 'Content',
};

const BUCKET_ORDER = ['schema', 'meta-header', 'citation', 'content'] as const;

function StatusPill({ count }: { count: number }) {
  if (count === 0) return <span className="text-xs text-[#475569]">No pending fixes</span>;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
      {count} pending
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-[6px] p-6 animate-pulse">
      <div className="h-3 w-24 bg-gray-100 rounded mb-3" />
      <div className="h-8 w-16 bg-gray-200 rounded mb-2" />
      <div className="h-3 w-32 bg-gray-100 rounded" />
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function AfiOverviewPage({ domainId, onNavigate }: Props) {
  const { data: counts, isLoading: countsLoading } = useQuery({
    queryKey: ['afi-pending-fixes', domainId],
    queryFn: () => api.afi.pendingFixCounts(domainId),
    staleTime: 30_000,
  });

  const { data: activity } = useQuery({
    queryKey: ['afi-recent-deployments', domainId, 5],
    queryFn: () => api.afi.recentDeployments(domainId, 5),
    staleTime: 30_000,
  });

  const totalPending = counts?.total ?? 0;

  return (
    <div className="min-h-screen bg-[#F1F5F9] p-8">
      {/* Page header */}
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#1D9E75] mb-1">
          Auto-fix
        </p>
        <h1 className="text-2xl font-semibold text-[#0F172A]">Overview</h1>
        {totalPending > 0 && (
          <p className="text-sm text-[#475569] mt-1">
            {totalPending} fix{totalPending !== 1 ? 'es' : ''} awaiting your review
          </p>
        )}
      </div>

      {/* Pending fix counts per bucket */}
      <section aria-labelledby="fix-counts-heading" className="mb-8">
        <h2 id="fix-counts-heading" className="text-sm font-semibold text-[#0F172A] mb-4">
          Fixes pending review
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {countsLoading
            ? BUCKET_ORDER.map((b) => <SkeletonCard key={b} />)
            : BUCKET_ORDER.map((bucket) => {
                const count = counts?.counts[bucket] ?? 0;
                const isClickable = count > 0 && bucket !== 'content';
                return (
                  <div
                    key={bucket}
                    className={`bg-white rounded-[6px] p-6 transition-shadow ${
                      isClickable
                        ? 'cursor-pointer hover:shadow-[0_2px_8px_rgba(15,23,42,0.06),0_0_0_1px_rgba(15,23,42,0.04)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]'
                        : ''
                    }`}
                    onClick={
                      isClickable ? () => onNavigate('afi-review', bucket) : undefined
                    }
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={
                      isClickable
                        ? (e) => { if (e.key === 'Enter' || e.key === ' ') onNavigate('afi-review', bucket); }
                        : undefined
                    }
                  >
                    <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#475569] mb-2">
                      {BUCKET_LABELS[bucket]}
                    </p>
                    <p className="text-3xl font-semibold text-[#0F172A] mb-2">{count}</p>
                    <StatusPill count={count} />
                    {isClickable && (
                      <p className="text-[11px] text-[#1D9E75] mt-3 font-medium">Review →</p>
                    )}
                  </div>
                );
              })}
        </div>
      </section>

      {/* Quick actions */}
      {totalPending > 0 && (
        <section className="mb-8">
          <button
            onClick={() => onNavigate('afi-review')}
            className="bg-[#0F172A] text-white text-sm font-medium px-5 py-2.5 rounded-[6px] hover:bg-[#0F6E56] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
          >
            Go to review queue
          </button>
        </section>
      )}

      {/* Recent deployment activity */}
      <section aria-labelledby="activity-heading">
        <div className="flex items-center justify-between mb-4">
          <h2 id="activity-heading" className="text-sm font-semibold text-[#0F172A]">
            Recent activity
          </h2>
          <button
            onClick={() => onNavigate('afi-activity')}
            className="text-xs text-[#1D9E75] hover:text-[#0F6E56] font-medium transition-colors"
          >
            View all
          </button>
        </div>

        {!activity || activity.events.length === 0 ? (
          <div className="bg-white rounded-[6px] p-6 text-center">
            <p className="text-sm text-[#475569]">No deployments yet for this domain.</p>
            <p className="text-xs text-[#94A3B8] mt-1">
              Fixes will appear here once AFI starts dispatching.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-[6px] divide-y divide-[#E2E8F0]">
            {activity.events.map((event) => (
              <div key={event.id} className="flex items-center justify-between px-6 py-4 min-h-[56px]">
                <div className="flex items-center gap-3">
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      event.dispatchStatus === 'dispatched'
                        ? 'bg-[#1D9E75]'
                        : event.dispatchStatus === 'failed' || event.dispatchStatus === 'abandoned'
                        ? 'bg-red-400'
                        : 'bg-amber-400'
                    }`}
                  />
                  <div>
                    <p className="text-sm text-[#0F172A] capitalize">
                      {event.fixBucket.replace('_', ' ')} fix — {event.dispatchStatus}
                    </p>
                    {event.failureReason && (
                      <p className="text-xs text-red-500 mt-0.5">{event.failureReason}</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-[#94A3B8] shrink-0 ml-4">
                  {formatRelativeTime(event.dispatchedAt)}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
